import { useClientPortalAPI } from "@certn/api";
import { Text } from "@certn/cascadia";
import {
  BasicButton,
  Container,
  Layout,
  Loader,
  MediaQuery,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from "@certn/cascadia-react";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Route, Routes, useLocation, useResolvedPath } from "react-router-dom";
import { createIncludesTabIdMapper } from "@certn/utils";

import { PaymentProfilesTab } from "./PaymentProfilesTab";
import { AddPaymentProfileModal } from "./components/AddPaymentProfileModal";
import { AppFooterLinks } from "../../components/app-footer/AppFooterLinks";
import { AppHeader } from "../../components/app-header/AppHeader";
import { useClientAccount } from "../../hooks/useClientAccount";
import { BillingNavigation } from "../../routes/billing/BillingNavigation";
import { PageTitle } from "../billing-profiles/components/PageTitle";

export function PaymentProfilesPage() {
  const { t } = useTranslation();
  const { client } = useClientAccount();
  const { pathname } = useLocation();
  const url = useResolvedPath("").pathname;
  const [showNewPaymentModal, setShowNewPaymentModal] = useState(false);
  const [pinnedCustomerAccountId, setPinnedCustomerAccountId] = useState<
    string | null
  >(null);
  const [
    pinnedInitialPaymentProfileCount,
    setPinnedInitialPaymentProfileCount,
  ] = useState(0);
  const { data, mutate } = useClientPortalAPI(
    client?.id
      ? `/api/internal/client-portal/customer-accounts/${client.id}/payment-profiles/`
      : null
  );
  // A missing client id changes the SWR key and clears `data`. While the
  // add-card modal is open, keep the last list so that gap cannot replace
  // this page with a loader. FR-1442.
  const [cachedList, setCachedList] = useState(data);

  useEffect(() => {
    if (data?.results) {
      setCachedList(data);
    }
  }, [data]);

  const list = data?.results
    ? data
    : showNewPaymentModal
      ? cachedList
      : undefined;
  const archivedUrl = `${url}/archived`;

  const mapSelectedTabId = createIncludesTabIdMapper(
    [{ substring: "/archived", id: "archived-tab" }],
    "active-tab"
  );

  const handleOpenAddPaymentProfileModal = () => {
    if (!client?.id) return;
    const activeList = data?.results ? data : cachedList;
    setPinnedCustomerAccountId(client.id);
    setPinnedInitialPaymentProfileCount(
      typeof activeList?.count === "number"
        ? activeList.count
        : activeList?.results?.length ?? 0
    );
    setShowNewPaymentModal(true);
  };
  const handleClose = () => {
    setShowNewPaymentModal(false);
    setPinnedCustomerAccountId(null);
    void mutate();
  };

  if (!list?.results && !showNewPaymentModal) {
    return <Loader />;
  }

  return (
    <Layout
      layout="wide"
      header={<AppHeader />}
      navigation={<BillingNavigation />}
      footer={<AppFooterLinks />}
    >
      <Container layout="narrow">
        <PageTitle
          actions={
            <BasicButton
              testID="add-new-payment-profile-button"
              onPress={() => handleOpenAddPaymentProfileModal()}
            >
              {t("New payment profile")}
            </BasicButton>
          }
        >
          {t("Payment profiles")}
        </PageTitle>
        <MediaQuery sm type="hide">
          <Text color="text-weak">
            <Trans>
              Effortlessly view and manage all payment methods linked to your
              account.
            </Trans>
          </Text>
        </MediaQuery>
        <Tabs
          gap="space-3"
          selectedKey={mapSelectedTabId(pathname)}
          testID="payment-profiles-tabs"
        >
          <TabList aria-label={t("Integration settings")}>
            <Tab
              id="active-tab"
              href={url}
              testID="active-payment-profiles-tab"
            >
              <Trans>Active</Trans>
            </Tab>
            <Tab
              id="archived-tab"
              href={archivedUrl}
              testID="archived-payment-profiles-tab"
            >
              <Trans>Archived</Trans>
            </Tab>
          </TabList>
          <TabPanel id={mapSelectedTabId(pathname)}>
            <Routes>
              <Route
                path=""
                element={
                  <PaymentProfilesTab
                    paymentProfiles={list?.results || []}
                    statusFilter="ACTIVE"
                    mutate={mutate}
                  />
                }
              />
              <Route
                path="/archived"
                element={
                  <PaymentProfilesTab
                    paymentProfiles={list?.results || []}
                    statusFilter="INACTIVE"
                    mutate={mutate}
                  />
                }
              />
            </Routes>
          </TabPanel>
        </Tabs>
        {showNewPaymentModal && pinnedCustomerAccountId && (
          <AddPaymentProfileModal
            customerAccountId={pinnedCustomerAccountId}
            handleClose={() => handleClose()}
            includeTitle
            initialPaymentProfileCount={pinnedInitialPaymentProfileCount}
          />
        )}
      </Container>
    </Layout>
  );
}
